/**
 * Helper per costruire icone "pie chart" Leaflet (divIcon) — usate sia per i
 * pin singoli cliente (proporzionali agli € per stato preventivo) sia per i
 * cluster (somma degli stati di tutti i clienti aggregati).
 *
 * Stati semantici:
 *   - draft   → bianco
 *   - sent    → giallo
 *   - won     → verde (include "contracted")
 *   - lost    → rosso
 *
 * Se un solo stato ha amount > 0, l'icona è un cerchio pieno di quel colore
 * (più leggibile di una "torta" a 1 fetta).
 */
import L from 'leaflet';

export type StatusKey = 'draft' | 'sent' | 'won' | 'lost';

export const STATUS_COLOR: Record<StatusKey, string> = {
  draft: '#ffffff',
  sent:  '#facc15', // yellow-400
  won:   '#16a34a', // green-600
  lost:  '#dc2626', // red-600
};

export const STATUS_LABEL: Record<StatusKey, string> = {
  draft: 'Bozza',
  sent:  'Inviato',
  won:   'Vinto',
  lost:  'Perso',
};

export const STATUS_ORDER: StatusKey[] = ['won', 'sent', 'draft', 'lost'];

export interface PieSlice {
  status: StatusKey;
  amount: number;
}

interface BuildPieIconOptions {
  /** Raggio in px. */
  radius: number;
  /** Fette: solo gli stati con amount > 0 vengono renderizzati. */
  slices: PieSlice[];
  /** Bordo più marcato (selezione). */
  highlighted?: boolean;
  /** Testo centrale opzionale (count per cluster). */
  centerLabel?: string;
  /** Testo sotto il centro opzionale (totale € per cluster). */
  centerSubLabel?: string;
}

/**
 * Disegna un arco SVG con il path "Lxxx" + "Axxx" tra due angoli (in radianti),
 * partendo dal centro per ottenere uno spicchio chiuso.
 */
function buildSliceArc(
  cx: number, cy: number, r: number,
  startAngle: number, endAngle: number,
): string {
  const x1 = cx + r * Math.cos(startAngle);
  const y1 = cy + r * Math.sin(startAngle);
  const x2 = cx + r * Math.cos(endAngle);
  const y2 = cy + r * Math.sin(endAngle);
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}

export function buildPieIcon({
  radius,
  slices,
  highlighted = false,
  centerLabel,
  centerSubLabel,
}: BuildPieIconOptions): L.DivIcon {
  const nonZero = slices.filter(s => s.amount > 0);
  const total = nonZero.reduce((acc, s) => acc + s.amount, 0);
  const size = radius * 2;
  const borderWidth = highlighted ? 3 : 2;
  const borderColor = highlighted ? '#1d4ed8' : '#ffffff';

  // Caso degenere: nessuno stato con valore → cerchio grigio chiaro
  if (total <= 0) {
    const html = `
      <div style="
        width:${size}px;height:${size}px;border-radius:50%;
        background:#e5e7eb;
        border:${borderWidth}px solid ${borderColor};
        box-shadow:0 2px 6px rgba(0,0,0,0.35);
      "></div>
    `;
    return L.divIcon({
      html, className: 'dryconfig-pie-pin',
      iconSize: [size, size], iconAnchor: [radius, radius],
    });
  }

  // Caso 1 sola fetta: cerchio pieno (più leggibile di una "torta" intera)
  if (nonZero.length === 1) {
    const color = STATUS_COLOR[nonZero[0].status];
    const html = `
      <div style="
        width:${size}px;height:${size}px;border-radius:50%;
        background:${color};
        border:${borderWidth}px solid ${borderColor};
        box-shadow:0 2px 6px rgba(0,0,0,0.35);
        display:flex;flex-direction:column;align-items:center;justify-content:center;
        color:${nonZero[0].status === 'draft' ? '#111827' : '#ffffff'};
        font-family:ui-sans-serif,system-ui,-apple-system;font-weight:600;line-height:1;
        ${highlighted ? 'outline:2px solid #1d4ed8;outline-offset:2px;' : ''}
      ">
        ${centerLabel ? `<div style="font-size:${Math.max(11, radius * 0.55)}px;">${centerLabel}</div>` : ''}
        ${centerSubLabel ? `<div style="font-size:${Math.max(8, radius * 0.30)}px;opacity:0.9;margin-top:2px;">${centerSubLabel}</div>` : ''}
      </div>
    `;
    return L.divIcon({
      html, className: 'dryconfig-pie-pin',
      iconSize: [size, size], iconAnchor: [radius, radius],
    });
  }

  // Caso pie chart multi-fetta: SVG con archi proporzionali agli amount,
  // ordinati in STATUS_ORDER per coerenza visiva tra pin
  const cx = radius, cy = radius;
  // Per SVG, l'asse y è invertito: parto dall'alto (-PI/2)
  let cursor = -Math.PI / 2;
  const ordered = STATUS_ORDER
    .map(s => nonZero.find(x => x.status === s))
    .filter((x): x is PieSlice => !!x);

  const paths: string[] = [];
  for (const slice of ordered) {
    const sliceAngle = (slice.amount / total) * Math.PI * 2;
    const start = cursor;
    const end = cursor + sliceAngle;
    paths.push(
      `<path d="${buildSliceArc(cx, cy, radius - borderWidth, start, end)}"
         fill="${STATUS_COLOR[slice.status]}"
         stroke="rgba(0,0,0,0.15)" stroke-width="0.5" />`,
    );
    cursor = end;
  }

  const centerTextSize = Math.max(11, radius * 0.42);
  const subTextSize = Math.max(8, radius * 0.26);
  const html = `
    <div style="
      position:relative;width:${size}px;height:${size}px;
      filter:drop-shadow(0 2px 4px rgba(0,0,0,0.35));
      ${highlighted ? 'outline:2px solid #1d4ed8;outline-offset:2px;border-radius:50%;' : ''}
    ">
      <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="display:block;">
        <circle cx="${cx}" cy="${cy}" r="${radius - borderWidth / 2}"
                fill="#ffffff" stroke="${borderColor}" stroke-width="${borderWidth}" />
        ${paths.join('\n')}
        <circle cx="${cx}" cy="${cy}" r="${radius - borderWidth / 2}"
                fill="none" stroke="${borderColor}" stroke-width="${borderWidth}" />
      </svg>
      ${(centerLabel || centerSubLabel) ? `
        <div style="
          position:absolute;inset:0;display:flex;flex-direction:column;
          align-items:center;justify-content:center;
          color:#111827;font-family:ui-sans-serif,system-ui,-apple-system;
          font-weight:700;line-height:1;pointer-events:none;
          text-shadow:0 1px 2px rgba(255,255,255,0.95);
        ">
          ${centerLabel ? `<div style="font-size:${centerTextSize}px;">${centerLabel}</div>` : ''}
          ${centerSubLabel ? `<div style="font-size:${subTextSize}px;margin-top:1px;opacity:0.85;">${centerSubLabel}</div>` : ''}
        </div>
      ` : ''}
    </div>
  `;
  return L.divIcon({
    html, className: 'dryconfig-pie-pin',
    iconSize: [size, size], iconAnchor: [radius, radius],
  });
}

export function euroShort(v: number): string {
  if (!Number.isFinite(v) || v === 0) return '€0';
  if (v >= 1_000_000) return `€${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `€${(v / 1000).toFixed(1)}k`;
  return `€${Math.round(v)}`;
}
